import { useState } from "react";
import { useSnackbar } from "notistack";
import { buildCopyAriaLabel, getCopyAnalyticsAttributes } from "@/lib/copyAriaLabel";
import { copyTextToClipboard } from "@/lib/copyToClipboard";

export interface ListTableCopyButtonProps {
  textToCopy: string;
  copyFieldLabel: string;
  copyFieldId?: string;
}

/**
 * Copy control for People/Facilities list tables. Uses a native icon span (not Lit `md-icon`)
 * so Amplitude elementInteractions records the labeled `<button>` as the click target.
 */
export default function ListTableCopyButton({ textToCopy, copyFieldLabel, copyFieldId }: ListTableCopyButtonProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await copyTextToClipboard(textToCopy);
    } catch {
      enqueueSnackbar("Failed to copy", { variant: "error" });
      return;
    }

    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000);
  };

  return (
    <div className="relative flex items-center gap-1">
      {showTooltip && (
        <div className="absolute bottom-6 bg-sys-brand-inverse-surface text-sys-brand-inverse-on-surface px-2 py-1 rounded whitespace-nowrap">
          Copied
        </div>
      )}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={buildCopyAriaLabel({ copyFieldLabel })}
        {...getCopyAnalyticsAttributes({ copyFieldLabel, copyFieldId })}
        className="flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0"
      >
        <span
          aria-hidden="true"
          className="material-symbols-rounded pointer-events-none text-[20px] leading-none text-sys-brand-on-primary-container"
          style={{ fontFamily: "Material Symbols Rounded", fontVariationSettings: "'FILL' 0", fontWeight: 400 }}
        >
          content_copy
        </span>
      </button>
    </div>
  );
}
